import { useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter 233 (Carlos) — Refrescar UN contacto Alegra (cliente o proveedor)
 * sin esperar al cron horario / sync semanal.
 *
 * Útil cuando se edita un cliente/proveedor en Alegra Web (nombre, dirección,
 * email, teléfono, condiciones de pago) y se quiere ver el cambio en el ERP
 * inmediatamente.
 *
 * Acepta RUT (con o sin guión) o Alegra ID. Hace POST a
 * `/api/alegra/contacto/refresh` que internamente busca en Alegra y upsertea
 * en `alegra_contacts`.
 */
export default function RefreshContactoCard() {
  const [input, setInput] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [result, setResult] = useState(null);

  const handleRefresh = async () => {
    const q = input.trim();
    if (!q) {
      toast.error('Ingresa un RUT o un Alegra ID');
      return;
    }
    setRefreshing(true);
    setResult(null);
    try {
      // Heurística: si es 100% numérico (sin '-' ni 'K'), tratamos como alegra_id.
      // Si tiene formato típico de RUT chileno (números + '-' + dv), va como rut.
      const isPureNumeric = /^\d+$/.test(q);
      const payload = isPureNumeric ? { alegra_id: q } : { rut: q };
      const res = await api.post('/alegra/contacto/refresh', payload);
      setResult(res.data);
      const c = res.data?.contact;
      toast.success(`${res.data.action === 'created' ? 'Contacto creado' : 'Contacto refrescado'}: ${c?.nombre || ''}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Error desconocido';
      toast.error(detail);
      setResult({ error: detail });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded" data-testid="refresh-contacto-card">
      <div className="font-semibold text-violet-900 text-sm flex items-center gap-2 mb-1">
        <RefreshCw className="w-4 h-4" /> Refrescar Contacto Puntual (cliente o proveedor)
      </div>
      <div className="text-xs text-violet-700 mb-2">
        Si editaste a alguien en Alegra Web y necesitas ver el cambio AHORA (sin esperar al cron horario), pega su RUT o Alegra ID.
      </div>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-violet-600" />
          <Input
            className="pl-9 bg-white"
            placeholder="Ej: 76954800-9  o  19"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRefresh(); }}
            data-testid="refresh-contacto-input"
          />
        </div>
        <Button
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing || !input.trim()}
          className="bg-violet-600 hover:bg-violet-700 text-white"
          data-testid="refresh-contacto-btn"
        >
          {refreshing ? (
            <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Refrescando...</>
          ) : (
            <><RefreshCw className="w-4 h-4 mr-1" /> Refrescar</>
          )}
        </Button>
      </div>

      {result?.contact && (
        <div className="mt-2 p-2 bg-white rounded border border-violet-100 text-xs" data-testid="refresh-contacto-result">
          <div className="font-semibold text-violet-900">{result.contact.nombre}</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-violet-800 mt-1">
            <div><span className="text-violet-500">RUT:</span> <span className="font-mono">{result.contact.identificacion || '—'}</span></div>
            <div><span className="text-violet-500">Alegra ID:</span> <span className="font-mono">{result.contact.alegra_id}</span></div>
            <div><span className="text-violet-500">Email:</span> {result.contact.email || '—'}</div>
            <div><span className="text-violet-500">Teléfono:</span> {result.contact.telefono || '—'}</div>
            <div className="col-span-2"><span className="text-violet-500">Tipo:</span> {(result.contact.tipo || []).join(', ') || '—'}</div>
            <div className="col-span-2"><span className="text-violet-500">Dirección:</span> {result.contact.direccion || '—'}</div>
          </div>
        </div>
      )}
    </div>
  );
}
